import { DatasetRecord, DisplayDataColumn, GDT } from "@/types";
import { defineStore } from "pinia";
import { filterReset, parseAccumulationItem } from "@/util/helpers";
import { AxiosResponse } from "axios";
import { Point } from "geojson";
import {
  useApi,
  useDataSource,
  useLayout,
  useLoading,
  useLocation,
  useMap,
  useOrgSettings,
} from "@/services";
import { useAxios } from "@/config";
import { v4 } from "uuid";

export interface GammaDataState {
  dataSources: string[];
  activeSourceIndex: number;
  accumulations: Record<string, DatasetRecord[]>;
  filterOperators: Record<string, GDT.FilterOperatorOption[]>;
  activeColumnType: string;
  selectedFilter: GDT.DataFilter;
  selectedRecord: null | DatasetRecord;
  useDatasetRecordGroups: boolean;
}

export const useGammaData = defineStore("gammaData", {
  state: (): GammaDataState => ({
    dataSources: [],
    activeSourceIndex: 0,
    accumulations: {},
    filterOperators: {},
    activeColumnType: "Integer",
    selectedFilter: filterReset as GDT.DataFilter,
    selectedRecord: null,
    useDatasetRecordGroups:
      Boolean(process.env.VUE_APP_USE_DATASET_RECORD_GROUPS) || false,
  }),
  actions: {
    fetchGeoJson(
      payload: GDT.DataRequest,
      queue = "default"
    ): Promise<
      AxiosResponse<string | GeoJSON.Feature | GeoJSON.FeatureCollection>
    > {
      return useLoading().handleRequest(
        useAxios().$http.post(
          `${payload.catalogUri}/${GDT.Action.GeoJson}`,
          payload.request
        ),
        queue
      );
    },
    fetchData(
      payload: Partial<GDT.GetDataRequest>,
      queue = "default"
    ): Promise<AxiosResponse<GDT.GetDataResponse>> {
      return useLoading().handleRequest(
        useAxios().$http.post(
          `${payload.catalogUri}/${GDT.Action.Data}`,
          payload
        ),
        queue
      );
    },
    fetchMeta(
      payload: Partial<GDT.GetDataRequest>,
      queue = "default"
    ): Promise<AxiosResponse<GDT.TableDefinition>> {
      return useLoading().handleRequest(
        useAxios().$http.post(
          `${payload.catalogUri}/${GDT.Action.Meta}`,
          payload
        ),
        queue
      );
    },
    fetchMetaData(
      payload: Partial<GDT.GetDataRequest>,
      queue = "default"
    ): Promise<
      | AxiosResponse<{ meta: GDT.TableDefinition; data: GDT.GetDataResponse }>
      | Record<string, unknown>
    > {
      return useLoading().handleRequest(
        useAxios().$http.post(
          `${payload.catalogUri}/${GDT.Action.MetaData}`,
          payload
        ),
        queue
      );
    },
    async fetchDatasetRecord({
      query,
      lcId,
    }: {
      query: Record<string, any>;
      lcId: string;
    }) {
      if (lcId) {
        const dataSource = useDataSource(lcId)();
        const uniqueIdColumns = await dataSource
          .td()
          .then(() => dataSource.uniqueIdColumns);
        const filter: GDT.DataFilter[] =
          uniqueIdColumns.map((c) => ({
            fieldName: c.columnName,
            filterOperator: GDT.FilterOperator.EqualTo,
            conditionOperator: GDT.ConditionOperator.And,
            value: query[c.columnName],
          })) || [];
        return dataSource
          .fetchData(
            {
              filter,
              displayColumns: dataSource.columnDefinitions
                .map((c) => ({
                  name: c.columnName,
                  visible: dataSource.tableDefinition?.request.displayColumns
                    .map((dc: GDT.DisplayColumn) => dc.name)
                    .includes(c.columnName),
                }))
                .map((col, k) => ({
                  ...col,
                  orderInSelectList: k + 1,
                })),
            },
            {
              shouldUpdateData: false,
              requestType: "spatial",
            },
            "default"
          )
          .then((r) => r.rows?.[0] || [])
          .then(
            (data) =>
              new DatasetRecord(data, dataSource.columnDefinitions, {
                recordType: dataSource.type,
                source: dataSource.source,
                lcId: dataSource.name,
                geometry:
                  data[
                    dataSource.columnDefinitions.findIndex(
                      (c) => c.type === "Geometry"
                    )
                  ],
              })
          )
          .then((dr) => {
            if (!dr.data.length) {
              throw new Error("No data result");
            }
            useLayout().isLeftSidebarOpen = true;
            useLocation().updateLocation({
              record: dr,
              type: dr.recordType,
              geometry: dr.geometry as Point,
            });
          });
      }
    },
    updateRecord(
      payload: Partial<GDT.UpdateRecordRequest>,
      lcId: string
    ): Promise<AxiosResponse<unknown>> {
      const dataSource = useDataSource(lcId)();
      return useLoading().handleRequest(
        useAxios().$http.post(`${dataSource.catalogUri}/update`, payload),
        "record-update"
      );
    },
    updateDatasetRecord(query: any, lcId: string) {
      const dataSource = useDataSource(lcId)();
      return this.updateRecord(
        {
          catalogUri: dataSource.catalogUri,
          ...query,
        },
        lcId
      ).then(() => {
        useMap().isEditingCoordinates = false;
        this.fetchDatasetRecord({
          query: useLocation().location?.record?.query || {},
          lcId,
        });
      });
    },
    fetchAccumulationsRecord({
      lcId,
      coordinates,
      bufferValue,
    }: {
      lcId: string;
      coordinates: number[];
      bufferValue: number;
    }) {
      const dataSource = useDataSource(lcId)();
      const orgSettings = useOrgSettings();
      if (dataSource) {
        const contextColumn =
          dataSource.tableDefinition?.dataSetDefinition.columns.find(
            (c: GDT.ColumnDefinition) => c.context === "SumInsured"
          );
        if (contextColumn) {
          return dataSource
            .fetchData(
              {
                filter: [
                  {
                    functionName:
                      orgSettings.accumulations?.spatialFunction ??
                      "DistanceSphereFromCentroidToPointLessThanValue",
                    arguments: [
                      {
                        name: "field",
                        value: "geom",
                      },
                      {
                        name: "x",
                        value: coordinates[0],
                      },
                      {
                        name: "y",
                        value: coordinates[1],
                      },
                      {
                        name: "threshold",
                        value: bufferValue,
                      },
                      {
                        name: "srid",
                        value: "4326",
                      },
                    ],
                    conditionOperator: 0,
                  },
                ],
                displayColumns: [
                  {
                    name: contextColumn.columnName,
                    label: contextColumn.columnName,
                    orderInSelectList: 1,
                    visible: true,
                  },
                ],
              },
              {
                shouldUpdateData: false,
                requestType: "spatial",
              },
              "locationAccumulations"
            )
            .then((r) => ({
              datasetType: dataSource.source.dataSetType,
              businessLine: dataSource.source.businessLine,
              count: r.rows?.length,
              value: r.rows
                ?.map((e: any) => e[0] || 0)
                .reduce((a: number, b: number) => a + b, 0),
            }));
        }
      }
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(
            this.fetchAccumulationsRecord({ lcId, coordinates, bufferValue })
          );
        }, 1e3);
      });
    },
    fetchAccumulations(): Promise<void> | Promise<void[]> {
      const sources = this.dataSources
        .map((x) => useDataSource(x)())
        .filter((x) => useMap().dataBoundary || x.userFilters.length);
      if (sources.length) {
        return Promise.all(
          sources
            .filter((s) => s.name)
            .map((source) => {
              return source
                .fetchData(
                  {
                    displayColumns: [],
                  },
                  {
                    shouldUpdateData: false,
                    requestType: "accumulation",
                  },
                  "accumulations"
                )
                .then(({ rows }) => {
                  return this.updateAccumulations({
                    source:
                      useDataSource(source.name)().tableDefinition
                        ?.dataSetDefinition?.name || v4(),
                    rows:
                      rows?.map((row: unknown[]) =>
                        parseAccumulationItem(
                          row,
                          useDataSource(source.name)().columnDefinitions,
                          {
                            recordType: useDataSource(source.name)().type,
                            lcId: useDataSource(source.name)().name,
                          }
                        )
                      ) || [],
                  });
                })
                .catch((err) => {
                  console.log("failed fetching accumulations", err);
                });
            })
        );
      }
      return Promise.resolve();
    },
    async fetchFilterOperators() {
      this.filterOperators = (
        await useAxios().$http.get(useApi().filterOperators)
      ).data;
    },
    selectDataSource({ lcId, index }: { lcId?: string; index?: number }) {
      const i = lcId
        ? this.dataSources.findIndex((s) => s === lcId)
        : index || 0;
      if (this.activeSourceIndex !== i) {
        this.dataSources
          .filter((ds) => useDataSource(ds)().isActive)
          .forEach((ds) => {
            useDataSource(ds)().isActive = false;
          });
        this.activeSourceIndex = Math.max(i, 0);
        useDataSource(this.dataSources[i])().isActive = true;
      }
    },
    updateAccumulations(payload?: { source: string; rows: any[] }) {
      this.accumulations = payload
        ? {
            ...this.accumulations,
            [payload.source]: payload.rows,
          }
        : {};
    },
    addDataFilter(payload: { sourceName: string; filter: GDT.DataFilter }) {
      const source = useDataSource(payload.sourceName)();
      source.addFilter(payload.filter);
      this.fetchAccumulations();
      useMap().refreshLayerData(payload.sourceName);
    },
    removeDataFilter(payload: {
      sourceName: string;
      filter?: GDT.DataFilter;
      index?: number;
    }) {
      const source = useDataSource(payload.sourceName)();
      source.removeFilter(payload.filter, payload.index);
      this.fetchAccumulations();
      useMap().refreshLayerData(payload.sourceName);
    },
    async addDataSource(args: GDT.IGammaDataSource) {
      if (args.name) {
        const index = this.dataSources.findIndex((s) => s === args.name);
        if (index < 0) {
          this.dataSources.push(args.name);
        }
        await useDataSource(args.name)().init(args);
      }
    },
    removeDataSource(name: string) {
      this.dataSources = this.dataSources.filter((x) => x !== name);
      useDataSource(name)().$reset();
      useDataSource(name)().$dispose();

      this.selectDataSource({ index: 0 });
    },
    setTableDefinition(payload: {
      lcId: string;
      tableDefinition: GDT.TableDefinition;
    }) {
      const source = useDataSource(payload.lcId)();
      source.tableDefinition = payload.tableDefinition;
    },
  },
  getters: {
    activeSourceId(): string {
      return this.dataSources[this.activeSourceIndex];
    },
    hasDataSources(): boolean {
      return !!this.dataSources.length;
    },
    activeFilterOperators(): GDT.FilterOperatorOption[] {
      return this.filterOperators[this.activeColumnType] || [];
    },
    tableDefinition(): GDT.TableDefinition | null {
      return useDataSource(this.activeSourceId)().tableDefinition || null;
    },
    datasetDefinition(): GDT.DatasetDefinition | null {
      return this.tableDefinition?.dataSetDefinition || null;
    },
    columnDefinitions(): GDT.ColumnDefinition[] {
      return this.datasetDefinition?.columns || [];
    },
    policies(): DatasetRecord[] {
      return (
        Object.values(this.accumulations).flat() as DatasetRecord[]
      ).filter((r) => r.recordType === "Policy");
    },
    isGroupSourceInZoom(): boolean {
      const { minzoom, maxzoom } =
        (useOrgSettings().settings?.mapSettings?.layers || []).find(
          ({ id }) => id === "group_boundaries"
        ) || {};
      const currentMapZoom = useMap().mapPosition.zoom;
      return (
        (minzoom ? currentMapZoom >= minzoom : false) &&
        (maxzoom ? currentMapZoom <= maxzoom : false)
      );
    },
    visiblePolicies(): DatasetRecord[] {
      return this.policies.filter((p) =>
        useMap()
          .layerControls.filter((lc) => lc.isVisible)
          .map((lc) => lc.id)
          .includes(p.lcId)
      );
    },
    request(): null | GDT.DatasetRequest {
      return this.tableDefinition?.request || null;
    },
    displayColumns(): GDT.DisplayColumn[] {
      return this.request?.displayColumns || [];
    },
    activeDataDisplayColumns(): Map<string | undefined, DisplayDataColumn> {
      const dataDisplay = useOrgSettings().dataDisplayDict.get(
        this.activeSourceId
      );

      if (!dataDisplay) {
        return new Map<string, DisplayDataColumn>();
      }

      return dataDisplay.columns.reduce(
        (x, y) => x.set(y.columnName, y),
        new Map<string, DisplayDataColumn>()
      );
    },
    claims(): DatasetRecord[] {
      return (
        Object.values(this.accumulations).flat() as DatasetRecord[]
      ).filter((r) => r.recordType === "Claim");
    },
    visibleClaims(): DatasetRecord[] {
      return this.claims.filter((c) =>
        useMap()
          .layerControls.filter((lc) => lc.isVisible)
          .map((lc) => lc.id)
          .includes(c.lcId)
      );
    },
    applicableFilters(): GDT.FilterOperatorOption[] {
      const filterOperators = this.filterOperators[this.activeColumnType] || [];

      const dataDisplayColumn = this.activeDataDisplayColumns.get(
        this.selectedFilter.fieldName
      );

      if (dataDisplayColumn && dataDisplayColumn.supportedOperators) {
        const supportedSet = new Set(dataDisplayColumn.supportedOperators);
        return filterOperators.filter((x) => supportedSet.has(x.id));
      }

      return filterOperators;
    },
    hasPrecisionAccuracy(): boolean {
      return !!this.datasetDefinition?.columns.find(
        (c) => c.context === "LocationAccuracyV1"
      );
    },
  },
});
